/**
 * Promo code dashboard API.
 *
 * The Poofy project ships only `policy/poof.json` (no generated `lib/collections/**`), so
 * `poofy local-ui sync-collections` cannot emit typed wrappers for the promo collections.
 * Instead the dashboard reads/writes promo codes through the authenticated backend routes
 * (`/api/v1/promo-codes*`) — the same service-owned-write / policy-scoped-read split the rest
 * of the privileged dashboard uses for sessions, revenue, and tenant registration.
 */

import { createAuthenticatedApiClient, ApiRequestError } from '@/lib/api-client';
import { getIdToken } from '@pooflabs/web';
import { ADMIN_ADDRESS } from '@/lib/constants';

// ApiClient is not exported from api-client.ts; derive its type from the factory.
type AuthedApiClient = ReturnType<typeof createAuthenticatedApiClient>;

export type PromoScope = 'tenant' | 'global';
export type PromoStatus = 'active' | 'disabled' | 'expired';

export interface PromoCode {
  promoCodeId: string;
  code: string;
  scope: PromoScope;
  appid: string | null;
  percentOff: number;
  status: PromoStatus;
  maxRedemptions: number | null;
  redemptionCount: number;
  expiresAt: number | null;
  createdAt: number;
  updatedAt: number;
}

export interface PromoRedemption {
  sessionId: string;
  tenantUserId: string;
  originalAmountUsdCents: number;
  chargedAmountUsdCents: number;
  percentOff: number;
  redeemedAt: number;
}

export interface CreatePromoInput {
  scope: PromoScope;
  appid?: string;
  code?: string;
  percentOff: number;
  expiresAt?: number | null;
  maxRedemptions?: number | null;
}

export interface UpdatePromoInput {
  percentOff?: number;
  expiresAt?: number | null;
  maxRedemptions?: number | null;
  status?: PromoStatus;
}

/** True when the connected wallet is the Poofy Pay platform admin (mirrors the backend gate). */
export function isPlatformAdmin(walletAddress?: string | null): boolean {
  return !!walletAddress && walletAddress.toLowerCase() === ADMIN_ADDRESS.toLowerCase();
}

const PROMO_ERROR_MESSAGES: Record<string, string> = {
  promo_code_conflict: 'A code with that name already exists in this scope.',
  promo_code_percent_invalid: 'Discount must be a whole number between 1 and 100%.',
  promo_code_invalid: 'Enter a valid code (letters, numbers, dashes).',
  promo_code_expired: 'That code has expired.',
  promo_code_disabled: 'That code is disabled.',
  promo_code_exhausted: 'That code has reached its redemption limit.',
};

/** Friendly text for a promo route failure; falls back to the raw message. */
export function promoErrorMessage(err: unknown): string {
  const raw = err instanceof ApiRequestError ? err.message : err instanceof Error ? err.message : String(err);
  return PROMO_ERROR_MESSAGES[raw] ?? raw ?? 'Something went wrong.';
}

async function authed(walletAddress: string): Promise<AuthedApiClient> {
  const token = await getIdToken();
  if (!token) throw new ApiRequestError('Connect your wallet to manage promo codes.', { status: 401 });
  return createAuthenticatedApiClient(token, walletAddress);
}

/** List tenant-scoped codes for an appid, or global codes (`scope: 'global'`, admin only). */
export async function listPromoCodes(
  walletAddress: string,
  opts: { appid?: string; scope?: PromoScope },
): Promise<PromoCode[]> {
  const client = await authed(walletAddress);
  const params = new URLSearchParams();
  if (opts.scope === 'global') {
    params.set('scope', 'global');
  } else if (opts.appid) {
    params.set('appid', opts.appid);
  }
  const data = await client.get<{ rows: PromoCode[] }>(`/api/v1/promo-codes?${params.toString()}`);
  return data.rows ?? [];
}

export async function createPromoCode(walletAddress: string, input: CreatePromoInput): Promise<PromoCode> {
  const client = await authed(walletAddress);
  const data = await client.post<{ promoCode: PromoCode }>('/api/v1/promo-codes', input);
  return data.promoCode;
}

export async function updatePromoCode(
  walletAddress: string,
  promoCodeId: string,
  input: UpdatePromoInput,
): Promise<PromoCode> {
  const client = await authed(walletAddress);
  const data = await client.post<{ promoCode: PromoCode }>(
    `/api/v1/promo-codes/${encodeURIComponent(promoCodeId)}/update`,
    input,
  );
  return data.promoCode;
}

export async function expirePromoCode(walletAddress: string, promoCodeId: string): Promise<void> {
  const client = await authed(walletAddress);
  await client.post(`/api/v1/promo-codes/${encodeURIComponent(promoCodeId)}/expire`, {});
}

export async function listRedemptions(walletAddress: string, promoCodeId: string): Promise<PromoRedemption[]> {
  const client = await authed(walletAddress);
  const data = await client.get<{ rows: PromoRedemption[] }>(
    `/api/v1/promo-codes/${encodeURIComponent(promoCodeId)}/redemptions`,
  );
  return data.rows ?? [];
}
